<script lang="ts" setup generic="ListItemType extends GenericListItemType">
import { GenericListHeading, GenericListItemType } from '@/core/models/generic-list.interface';

//#region Props & Emits
defineProps<{
   name: string;
   hasActions: boolean;
   item: any;
   advancedHeadings: boolean;
   isSelected: boolean;
   visibleHeadings: Array<GenericListHeading<ListItemType>>;
   isDragging: boolean;
   dragEnterIndex: number;
   getIsSelectable?: (item: ListItemType) => boolean;
}>();

const emits = defineEmits<{
   selectItem: [ListItemType];
   dragEnter: [{ event: DragEvent; index: number }];
}>();
//#endregion

//#region Methods
function getPropertyValue(key: string, item: ListItemType) {
   const value = getPropertyByKey(key, item);

   if (value === undefined || value === null) return '-';
   return value;
}
//#endregion
</script>

<template>
   <tr :class="item.classes">
      <td
         v-if="hasActions"
         class="-pinned"
         :class="{ '-list-item-disabled': getIsSelectable ? !getIsSelectable(item) : false }"
         :style="{ position: 'sticky', left: 0 }"
      >
         <div class="d-flex align-items-center justify-content-center w-100">
            <NxtCheckbox
               v-if="getIsSelectable ? getIsSelectable(item) : true"
               :name="`select-${name}`"
               :model-value="!!isSelected"
               @update:model-value="emits('selectItem', item)"
            />
         </div>
      </td>

      <slot v-if="!advancedHeadings" name="listItem" v-bind="{ item }"></slot>

      <template v-else>
         <td
            v-for="(heading, colIndex) in visibleHeadings"
            :key="heading.key"
            :class="[{ '-drag-entered': isDragging && dragEnterIndex === colIndex, '-pinned': heading.pinned }]"
            :style="{ ...heading.style, zIndex: heading.style?.zIndex ? +heading.style.zIndex - 1 : 1 }"
            @dragenter="emits('dragEnter', { event: $event, index: colIndex })"
         >
            <slot :name="heading.key as keyof ListItemType" v-bind="{ heading, item }">
               <template v-if="heading.displayMethod">
                  {{ heading.displayMethod(item) || '-' }}
               </template>
               <template v-else-if="heading.displayKey">
                  {{ getPropertyValue(heading.displayKey, item) }}
               </template>
               <template v-else-if="heading.action">
                  <NxtIconButton
                     v-if="heading.actionPermitted ? heading.actionPermitted(item) : true"
                     :name="heading.actionIcon"
                     :hover-text="typeof heading.actionHoverText === 'function' ? heading.actionHoverText(item) : heading.actionHoverText"
                     :disabled="typeof heading.actionDisabled === 'function' ? !!heading.actionDisabled(item) : !!heading.actionDisabled"
                     @on-click="heading.action?.(item)"
                  />
               </template>
               <template v-else-if="heading.key && typeof heading.key === 'string'">
                  {{ getPropertyValue(heading.key, item) }}
               </template>
               <template v-else> - </template>
            </slot>
         </td>
      </template>
   </tr>
</template>
